import React, { useCallback, useEffect, useMemo, useState } from 'react';

import OrderEdit from '@components/shared/orders/OrderEdit';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useOrderItemColumns from '@hooks/operator/useOrderItemColumns';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useParams } from 'react-router-dom';

import { orderService } from '@services/operator/orderService';
import { contactAddressService } from '@services/operator/contactAddressService';
import bundleService from '@services/operator/bundleService';

const OperatorOrderEdit = () => {
  const { id } = useParams();
  const { merchants, merchantShippingTypes, merchantSalesChannels, merchantInventoryTypes, currencies, loading: globalLoading, setCurrentMerchant, currentMerchant, refreshMerchantShippingTypes } = useOperatorData();
  const { enabledColumns, availableColumns, updateColumns } = useOrderItemColumns();

  // 儲存訂單所屬的 merchant ID（由 OrderEdit 的 onOrderLoaded 回傳）
  const [orderMerchantId, setOrderMerchantId] = useState(null);

  // onOrderLoaded: 只存 merchantId，不做 merchant 解析（避免 stale closure）
  const handleOrderLoaded = useCallback((orderData) => {
    if (orderData?.merchant?.id) {
      setOrderMerchantId(orderData.merchant.id);
    }
  }, []);

  // Reactive effect: 當 orderMerchantId 和 merchants 都就緒時，設定 currentMerchant
  // 不依賴 closure，所以不會有 stale merchants 問題
  useEffect(() => {
    if (!orderMerchantId || merchants.length === 0) return;
    if (orderMerchantId === currentMerchant?.id) return;

    const merchant = merchants.find(m => m.id === orderMerchantId);
    if (merchant) {
      setCurrentMerchant(merchant);
    }
  }, [orderMerchantId, merchants, currentMerchant?.id, setCurrentMerchant]);

  // 使用 hook 版本的 productService，會自動從 MerchantContext 注入 merchant_id
  const productService = useOperatorProductService();

  // 使用商戶特定的庫存類型，並轉換為 OrderEdit 期待的格式
  const effectiveInventoryTypes = useMemo(() => {
    return (merchantInventoryTypes || []).map(mit => mit.inventory_type).filter(Boolean);
  }, [merchantInventoryTypes]);

  // merchantDataLoading 條件：
  // 1. globalLoading: OperatorData 還在載入（merchants 等基礎資料）
  // 2. orderMerchantId 已知但 currentMerchant 還沒切換過去（等待 merchants 載入後解析）
  const merchantDataLoading = globalLoading ||
    (!!orderMerchantId && orderMerchantId !== currentMerchant?.id);

  return (
    <OrderEdit
      orderService={orderService}
      productService={productService}
      contactAddressService={contactAddressService}
      role="operator"
      orderId={id}
      salesChannels={merchantSalesChannels}
      shippingTypes={merchantShippingTypes}
      onReloadShippingTypes={refreshMerchantShippingTypes}
      inventoryTypes={effectiveInventoryTypes}
      merchantDataLoading={merchantDataLoading}
      merchantOptions={merchants}
      currencies={currencies}
      enabledOrderItemColumns={enabledColumns}
      availableOrderItemColumns={availableColumns}
      onUpdateOrderItemColumns={updateColumns}
      onOrderLoaded={handleOrderLoaded}
      bundleService={bundleService}
    />
  );
};

export default OperatorOrderEdit;
